import React, { useState } from "react";
import { Grid, Typography } from "@mui/joy";
import { Box, Button } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import { DownloadRounded } from "@mui-symbols-material/w300";
import { GridApiPro } from "@mui/x-data-grid-pro";
import ExportMenuButton from "../ExportMenuButton";
import { readGridColumnLayout } from "../Table/gridColumnLayout";
import { useApolloClient } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import moment from "moment";
import { BasicModalContent } from "@dreebit-registry/vsm-microservice-components";
import { SearchAssets } from "../../graphql/queries/searchAssets";
import { SearchAssetsDeferred } from "../../graphql/queries/searchAssetsDeferred";
import { SearchAssetsTotal } from "../../graphql/queries/searchAssetsTotal";
import { buildDynamicListFilter } from "../../utils/buildDynamicListFilter";
import { EXCEL_DATE_FORMAT, ExportColumnDef, ExportFormat, exportRows, toExcelDate } from "../../utils/listExport";
import { ExportMode, orderExportColumns } from "../../utils/tableColumnLayout";
import { DeferredFieldFlags, getDeferredFieldFlags, hasAnyDeferredFlag } from "../../utils/iotColumnFields";
import { filterColumnsForIndustry } from "../../utils/industryColumns";
import { formatValueWithUnit } from "../../utils/formatValueWithUnit";
import { useIndustryType } from "../../hooks/useIndustryType";
import { computeRemainingSeconds, formatRemainingTime } from "../../utils/exportEta";
import { useSnackbar } from "../../provider/snackbar";
import { TypeDefinitionAttribute } from "../../types";
import { buildInstalledBasePathString } from "../../utils/installedBasePath";
import { Sort } from "../../codegen/graphql";
import { getIotStatusLabel } from "../../config/iotConnectionStatusConfig";
import { getSlaLevelLabel, getSlaStatusLabel } from "../../config/iotSlaConfig";
import { isSlaEnabled } from "../../utils/slaEnabled";

const EXPORT_PAGE_SIZE = 100;

// Backend date as text and as Excel date
const dateColumn = (getDate: (row: any) => any) => ({
    getValue: (row: any) => {
        const date = getDate(row);
        return date ? moment(date).format("DD.MM.YYYY") : "";
    },
    getTypedValue: (row: any) => {
        const date = getDate(row);
        return date ? toExcelDate(moment(date)) : null;
    },
    excelFormat: EXCEL_DATE_FORMAT,
});

// Due date from the remaining days, as text and as Excel date
const dueDateColumn = (getDays: (row: any) => number | null | undefined) => ({
    getValue: (row: any) => {
        const days = getDays(row);
        return days != null ? moment().add(days, "days").format("DD.MM.YYYY") : "";
    },
    getTypedValue: (row: any) => {
        const days = getDays(row);
        return days != null ? toExcelDate(moment().startOf("day").add(days, "days")) : null;
    },
    excelFormat: EXCEL_DATE_FORMAT,
});

// Score between 0 and 1 as rounded percentage text and as Excel percentage
const scoreColumn = (getScore: (row: any) => number | null | undefined) => ({
    getValue: (row: any) => {
        const score = getScore(row);
        return score != null ? `${Math.round(score * 100)}%` : "";
    },
    getTypedValue: (row: any) => {
        const score = getScore(row);
        return score != null ? Math.round(score * 100) / 100 : null;
    },
    excelFormat: "0%",
});

const buildExportColumnDefs = (t: TFunction): ExportColumnDef[] => [
    {
        field: "title",
        headerName: t("Asset Name"),
        getValue: (row) => row.attributes?.title || row.attributes?.productName || "",
    },
    {
        field: "serialNumber",
        headerName: t("Serial No."),
        getValue: (row) => row.attributes?.serialNumber || "",
    },
    {
        field: "semiconductorTool",
        headerName: t("Semiconductor Tool"),
        getValue: (row) => row.attributes?.semiconductorToolTitle || "",
    },
    {
        field: "semiconductorChamber",
        headerName: t("Semiconductor Chamber"),
        getValue: (row) => row.attributes?.semiconductorChamberTitle || "",
    },
    {
        field: "imei",
        headerName: t("IMEI"),
        getValue: (row) => row.attributes?.imei || "",
    },
    {
        field: "product",
        headerName: t("Product Name"),
        getValue: (row) => row.attributes?.productName || "",
    },
    {
        field: "productCode",
        headerName: t("Product Code"),
        getValue: (row) => row.attributes?.productCode || "",
    },
    {
        field: "manufacturer",
        headerName: t("Manufacturer"),
        getValue: (row) => row.attributes?.manufacturerName || "",
    },
    {
        field: "client",
        headerName: t("Company"),
        getValue: (row) => row.attributes?.clientName || "",
    },
    {
        field: "country",
        headerName: t("Country"),
        getValue: (row) => row.attributes?.countryName || "",
    },
    {
        field: "city",
        headerName: t("City"),
        getValue: (row) => row.attributes?.city || "",
    },
    {
        field: "location",
        headerName: t("Location"),
        // Full path from the deferred pass; the index's parent title if that row is missing.
        getValue: (row) =>
            buildInstalledBasePathString(row.assetLocation?.installedBaseNode) ||
            row.attributes?.parentNodeTitle ||
            "",
    },
    {
        field: "iotConnection",
        headerName: t("IoT Asset Status"),
        getValue: (row) =>
            getIotStatusLabel(row.attributes?.iotConnectionStatus, t, { forExport: true }),
    },
    {
        field: "firmwareVersion",
        headerName: t("Firmware Version"),
        getValue: (row) => row.attributes?.firmwareVersion || "",
    },
    {
        field: "modemFirmwareVersion",
        headerName: t("Modem Firmware Version"),
        getValue: (row) => row.attributes?.modemFirmwareVersion || "",
    },
    {
        field: "iotHubConnectivity",
        headerName: t("IoT Hub Connectivity"),
        getValue: (row) => row.attributes?.iotHubConnectivityStatus || "",
    },
    // SLA metrics — only selected with REACT_APP_ENABLE_SLA.
    ...(isSlaEnabled()
        ? [
              {
                  field: "slaStatus",
                  headerName: t("SLA Status"),
                  getValue: (row) => getSlaStatusLabel(row.attributes?.iotSlaStatus, t),
              },
              {
                  field: "slaLevel",
                  headerName: t("SLA Level"),
                  getValue: (row) => getSlaLevelLabel(row.attributes?.iotSlaLevel, t),
              },
              {
                  field: "connectivityQuality",
                  headerName: t("Connectivity Quality"),
                  getValue: (row) => getSlaLevelLabel(row.attributes?.iotConnectivityQuality, t),
              },
          ] as ExportColumnDef[]
        : []),
    {
        field: "simStatus",
        headerName: t("IoT Connection Status"),
        getValue: (row) => {
            const signalStrength = row.assetLocation?.iotConnection?.networkInformation?.signalStrength;
            return signalStrength != null ? String(signalStrength) : "";
        },
        getTypedValue: (row) => row.assetLocation?.iotConnection?.networkInformation?.signalStrength,
    },
    {
        field: "simNetwork",
        headerName: t("SIM Network"),
        getValue: (row) => row.assetLocation?.iotConnection?.networkInformation?.currentStandard || "",
    },
    {
        field: "networkProvider",
        headerName: t("Network Provider"),
        getValue: (row) => row.assetLocation?.iotConnection?.networkInformation?.networkProvider || "",
    },
    {
        field: "maintenance",
        headerName: t("Maintenance"),
        getValue: (row) => {
            if (row.metrics?.maintenanceOverdue) return t("Overdue");
            if (row.metrics?.maintenanceDueSoon) return t("Due");
            return "";
        },
    },
    {
        field: "lastMaintenanceDate",
        headerName: t("Last Maintenance"),
        ...dateColumn((row) => row.metrics?.maintenanceSchedule?.lastMaintenanceDate),
    },
    {
        field: "lastExhaustFilterChangeDate",
        headerName: t("Last Exhaust Filter Change"),
        ...dateColumn((row) => row.metrics?.maintenanceSchedule?.lastExhaustFilterChangeDate),
    },
    {
        field: "lastOilAndFilterChangeDate",
        headerName: t("Last Oil and Filter Change"),
        ...dateColumn((row) => row.metrics?.maintenanceSchedule?.lastOilAndFilterChangeDate),
    },
    {
        field: "lastRemanufacturingDate",
        headerName: t("Last Remanufacturing"),
        ...dateColumn((row) => row.metrics?.maintenanceSchedule?.lastRemanufacturingDate),
    },
    {
        field: "daysUntilNextExhaustFilterChange",
        headerName: t("Next Exhaust Filter Change"),
        ...dueDateColumn((row) => row.metrics?.maintenanceSchedule?.daysUntilNextExhaustFilterChange),
    },
    {
        field: "daysUntilNextOilAndFilterChange",
        headerName: t("Next Oil and Filter Change"),
        ...dueDateColumn((row) => row.metrics?.maintenanceSchedule?.daysUntilNextOilAndFilterChange),
    },
    {
        field: "daysUntilNextRemanufacturing",
        headerName: t("Next Remanufacturing"),
        ...dueDateColumn((row) => row.metrics?.maintenanceSchedule?.daysUntilNextRemanufacturing),
    },
    {
        field: "daysUntilNextService",
        headerName: t("Next Service"),
        ...dueDateColumn((row) => row.metrics?.maintenanceSchedule?.daysUntilNextService),
    },
    {
        field: "harshCondition",
        headerName: t("Condition"),
        getValue: (row) => row.metrics?.harshConditionThreshold || "",
    },
    {
        field: "harshConditionScore",
        headerName: t("Harsh Condition Score"),
        ...scoreColumn((row) => row.metrics?.harshConditionScore),
    },
    {
        field: "exhaustFilter",
        headerName: t("Exhaust Filter"),
        getValue: (row) => row.metrics?.exhaustFilterConditionThreshold || "",
    },
    {
        field: "exhaustFilterConditionScore",
        headerName: t("Exhaust Filter Score"),
        ...scoreColumn((row) => row.metrics?.exhaustFilterConditionScore),
    },
    {
        field: "missingSensor",
        headerName: t("Missing Sensor Values"),
        getValue: (row) => row.metrics?.missingSensorValues ? t("Missing sensor values") : "",
    },
    {
        field: "lastMissingSensorValuesAt",
        headerName: t("Last Missing Sensor Values"),
        ...dateColumn((row) => row.metrics?.lastMissingSensorValuesAt),
    },
    {
        field: "sensorValueOutOfRange",
        headerName: t("Sensor Value out of Range"),
        getValue: (row) => row.metrics?.sensorValueOutOfRange ? t("Out of range") : "",
    },
    {
        field: "lastSensorValueOutOfRangeAt",
        headerName: t("Last Sensor Value out of Range"),
        ...dateColumn((row) => row.metrics?.lastSensorValueOutOfRangeAt),
    },
    // No locale here on purpose: `1002.5 hPa` stays parseable in a CSV.
    {
        field: "sensorValueOutOfRangeParameter",
        headerName: t("Out of Range Parameter"),
        getValue: (row) => row.metrics?.sensorValueOutOfRangeDetails?.telemetryParameterLabel ?? "",
    },
    {
        field: "sensorValueOutOfRangeRecordedValue",
        headerName: t("Out of Range Value"),
        getValue: (row) => {
            const details = row.metrics?.sensorValueOutOfRangeDetails;
            return formatValueWithUnit(details?.recordedSensorValue, details?.recordedSensorValueUnit);
        },
    },
    {
        field: "sensorValueOutOfRangeLowerThreshold",
        headerName: t("Lower Threshold"),
        getValue: (row) => {
            const details = row.metrics?.sensorValueOutOfRangeDetails;
            return formatValueWithUnit(details?.lowerSensorThresholdValue, details?.lowerSensorThresholdValueUnit);
        },
    },
    {
        field: "sensorValueOutOfRangeUpperThreshold",
        headerName: t("Upper Threshold"),
        getValue: (row) => {
            const details = row.metrics?.sensorValueOutOfRangeDetails;
            return formatValueWithUnit(details?.upperSensorThresholdValue, details?.upperSensorThresholdValueUnit);
        },
    },
    {
        field: "vsdCandidate",
        headerName: t("VSD Candidate"),
        getValue: (row) => row.metrics?.vsdCandidateThreshold || "",
    },
    {
        field: "vsdCandidateScore",
        headerName: t("VSD Score"),
        ...scoreColumn((row) => row.metrics?.vsdCandidateScore),
    },
];

interface Props {
    filter?: Record<string, any>;
    searchParams?: { query: string; fields: string[] };
    attributes?: TypeDefinitionAttribute[];
    staticFilters?: Record<string, any>;
    /** The table's grid: the CSV columns follow its order and visibility. */
    apiRef?: React.MutableRefObject<GridApiPro>;
    /** The table's current sort, so the CSV rows come in the order the table shows. */
    sort?: Sort;
}

const AssetExportButton = ({ filter, searchParams, attributes, staticFilters, apiRef, sort }: Props) => {
    const { t } = useTranslation();
    const client = useApolloClient();
    const snackbar = useSnackbar();
    const industryType = useIndustryType();
    const [isExporting, setIsExporting] = useState(false);
    const [iotConfirm, setIotConfirm] = useState<{
        exportColumns: ExportColumnDef[];
        deferredFlags: DeferredFieldFlags;
        format: ExportFormat;
    } | null>(null);

    // Everything but the location path and the network information comes from
    // the search index in the first pass. Those two are fetched in a separate,
    // second pass; network information is resolved by slow backend services, so
    // exports that include it get a confirmation hint first.
    const handleExport = (mode: ExportMode, format: ExportFormat) => {
        const columnDefs = filterColumnsForIndustry(buildExportColumnDefs(t), industryType);
        const exportColumns = orderExportColumns(columnDefs, readGridColumnLayout(apiRef), mode);
        const deferredFlags = getDeferredFieldFlags(exportColumns.map((c) => c.field));

        if (deferredFlags.withNetworkInfo) {
            setIotConfirm({ exportColumns, deferredFlags, format });
            return;
        }
        runExport(exportColumns, deferredFlags, format);
    };

    const runExport = (exportColumns: ExportColumnDef[], deferredFlags: DeferredFieldFlags, format: ExportFormat) => {
        setIsExporting(true);
        snackbar.startLocalProgress(t("Preparing export..."));

        const builtFilter = buildDynamicListFilter(
            filter || {},
            (attributes || []).filter((a) => a.index !== "search"),
            staticFilters
        );
        // Same rule as the list: without a search term the search object is omitted entirely
        // ($query is nullable), so the export covers exactly the rows the table shows.
        const queryVars = searchParams?.query
            ? { query: searchParams.query, fields: searchParams.fields }
            : undefined;

        // Capture snackbar methods in closure so they survive component unmount
        const { updateLocalProgress, finishLocalProgress, errorLocalProgress } = snackbar;

        const includeDeferred = hasAnyDeferredFlag(deferredFlags);
        // Pass-2 filter: network information alone only concerns assets with an
        // IoT connection, so the pass is restricted to them instead of paying the
        // per-page resolver cost across the whole fleet. A user filter on the IoT
        // connection stays as-is. The location path concerns every row.
        const deferredFilter =
            deferredFlags.withPath || builtFilter?.iotEnabledDevice
                ? builtFilter
                : { ...builtFilter, iotEnabledDevice: { equals: true } };

        (async () => {
            try {
                // Fetch the second pass total up front (cheap count-only query) so
                // the progress denominator covers both passes and the bar only
                // reaches 100% when the file is actually ready.
                let deferredTotal = 0;
                if (includeDeferred) {
                    const totalResult = await client.query({
                        query: SearchAssetsTotal,
                        variables: { filter: deferredFilter, query: queryVars },
                        context: { clientName: "federatedGraphEndpoint" },
                        fetchPolicy: "no-cache",
                    });
                    deferredTotal = totalResult.data?.searchAssets?.total ?? 0;
                }

                // First pass: fetch first page to get total
                const firstResult = await client.query({
                    query: SearchAssets,
                    variables: {
                        filter: builtFilter,
                        query: queryVars,
                        pagination: { start: 0, limit: EXPORT_PAGE_SIZE },
                        sort,
                        withSla: isSlaEnabled(),
                    },
                    context: { clientName: "federatedGraphEndpoint" },
                    fetchPolicy: "no-cache",
                });

                const total: number = firstResult.data.searchAssets.total;
                const grandTotal = total + deferredTotal;
                let allRows: any[] = [
                    ...firstResult.data.searchAssets.searchAssetResults,
                ];
                updateLocalProgress(allRows.length, grandTotal);

                // Fetch remaining pages
                for (
                    let start = EXPORT_PAGE_SIZE;
                    start < total;
                    start += EXPORT_PAGE_SIZE
                ) {
                    const result = await client.query({
                        query: SearchAssets,
                        variables: {
                            filter: builtFilter,
                            query: queryVars,
                            pagination: { start, limit: EXPORT_PAGE_SIZE },
                            sort,
                            withSla: isSlaEnabled(),
                        },
                        context: { clientName: "federatedGraphEndpoint" },
                        fetchPolicy: "no-cache",
                    });
                    allRows = [
                        ...allRows,
                        ...result.data.searchAssets.searchAssetResults,
                    ];
                    updateLocalProgress(allRows.length, grandTotal);
                }

                // Second pass: fetch the deferred field groups and merge them
                // into the rows by assetLocation.id. errorPolicy "all" keeps
                // partial resolver failures from rejecting the whole page, and
                // each page gets one retry against transient gateway errors.
                // All export queries use no-cache: export pages have no reason
                // to fill the cache, and a failed network lookup nulls the
                // whole iotConnection.
                if (includeDeferred) {
                    const queryDeferredPage = (start: number) =>
                        client.query({
                            query: SearchAssetsDeferred,
                            variables: {
                                filter: deferredFilter,
                                query: queryVars,
                                pagination: { start, limit: EXPORT_PAGE_SIZE },
                                ...deferredFlags,
                            },
                            context: { clientName: "federatedGraphEndpoint" },
                            fetchPolicy: "no-cache",
                            errorPolicy: "all",
                        });

                    const deferredByLocationId = new Map<string, any>();
                    const deferredPagesTotal = Math.max(1, Math.ceil(deferredTotal / EXPORT_PAGE_SIZE));
                    const deferredStartedAt = Date.now();
                    let deferredFetched = 0;
                    let pagesDone = 0;
                    let start = 0;

                    updateLocalProgress(
                        total,
                        grandTotal,
                        deferredFlags.withNetworkInfo ? t("Loading IoT data...") : undefined
                    );

                    do {
                        let result;
                        try {
                            result = await queryDeferredPage(start);
                        } catch (error) {
                            console.error(`IoT export page at offset ${start} failed, retrying once:`, error);
                            await new Promise((resolve) => setTimeout(resolve, 2000));
                            result = await queryDeferredPage(start);
                        }
                        const searchAssets = result.data?.searchAssets;
                        if (!searchAssets) {
                            throw new Error(`IoT export page at offset ${start} returned no data`);
                        }

                        deferredTotal = searchAssets.total;
                        const results = searchAssets.searchAssetResults ?? [];
                        results.forEach((row: any) => {
                            const locationId = row.assetLocation?.id;
                            if (locationId) deferredByLocationId.set(locationId, row.assetLocation);
                        });
                        deferredFetched += results.length;
                        pagesDone += 1;
                        start += EXPORT_PAGE_SIZE;

                        const remainingSeconds = computeRemainingSeconds(
                            pagesDone,
                            deferredPagesTotal,
                            Date.now() - deferredStartedAt
                        );
                        updateLocalProgress(
                            Math.min(total + deferredFetched, grandTotal),
                            grandTotal,
                            remainingSeconds
                                ? t("{{time}} remaining", { time: formatRemainingTime(remainingSeconds) })
                                : undefined
                        );
                        if (!results.length) break;
                    } while (start < deferredTotal);

                    allRows = allRows.map((row) => {
                        const deferred = deferredByLocationId.get(row.assetLocation?.id);
                        if (!deferred) return row;
                        return {
                            ...row,
                            assetLocation: { ...row.assetLocation, ...deferred },
                        };
                    });
                }

                await exportRows({
                    format,
                    columns: exportColumns,
                    rows: allRows,
                    fileBaseName: "assets-export",
                    sheetName: t("Assets"),
                });

                finishLocalProgress(t("Export complete"));
            } catch (error) {
                console.error("Asset export failed:", error);
                errorLocalProgress(t("Export failed"));
            } finally {
                setIsExporting(false);
            }
        })();
    };

    return (
        <>
            <ExportMenuButton
                onExport={handleExport}
                disabled={isExporting}
                testId="asset-export-button"
            />
            <Dialog
                open={!!iotConfirm}
                onClose={() => setIotConfirm(null)}
                data-testid="asset-export-iot-warning"
                slotProps={{
                    paper: {
                        sx: { minWidth: "500px" },
                    },
                }}
            >
                <BasicModalContent
                    startIcon={<DownloadRounded />}
                    onClose={() => setIotConfirm(null)}
                    title={<Typography level="titleSmallLight">{t("Export")}</Typography>}
                >
                    <Box display="flex" flexDirection="column" gap="20px">
                        <Box
                            borderRadius="4px"
                            border="1px solid #ccc"
                            padding="12px 16px"
                            marginTop="8px"
                        >
                            <Typography level="body-md">
                                {t("This export includes IoT data, which takes long to load. The export may take several minutes.")}
                            </Typography>
                        </Box>
                        <Grid container spacing={1} sx={{ pt: "8px" }}>
                            <Grid xs={6}>
                                <Button
                                    fullWidth
                                    variant="outlined"
                                    onClick={() => setIotConfirm(null)}
                                >
                                    {t("Cancel")}
                                </Button>
                            </Grid>
                            <Grid xs={6}>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    startIcon={<DownloadRounded />}
                                    onClick={() => {
                                        if (!iotConfirm) return;
                                        const { exportColumns, deferredFlags, format } = iotConfirm;
                                        setIotConfirm(null);
                                        runExport(exportColumns, deferredFlags, format);
                                    }}
                                >
                                    {t("Export")}
                                </Button>
                            </Grid>
                        </Grid>
                    </Box>
                </BasicModalContent>
            </Dialog>
        </>
    );
};

export default AssetExportButton;
